import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useAuth } from "../contexts/AuthContext";
import {
  fetchBankReconciliation,
  manualReconcilePayment,
} from "../services/reconciliationService";
import {
  RECONCILIATION_FILTERS,
  canManualReconcile,
  filterReconciliationTransactions,
  normalizeReconciliationTransaction,
  summarizeReconciliation,
} from "./reconciliationViewModel";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const toDdMmYyyy = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}-${month}-${year}` : null;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", "");
};

const formatMoney = (value, currency = "TZS") => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value ?? "-";
  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)} ${currency}`;
};

const chipColor = (status) => {
  if (["PROCESSED", "MATCHED", "MANUALLY_RECONCILED", "ALLOCATED"].includes(status)) {
    return "success";
  }
  if (["FAILED", "SECURITY_FAILED"].includes(status)) return "error";
  if (["UNMATCHED", "SUSPENSE", "NEEDS_REVIEW", "PENDING"].includes(status)) {
    return "warning";
  }
  return "default";
};

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
    <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: `${color}18`,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight={900} noWrap>
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const DetailField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={650} sx={{ overflowWrap: "anywhere" }}>
      {value ?? "-"}
    </Typography>
  </Box>
);

const DetailSection = ({ title, children }) => (
  <Box>
    <Typography variant="subtitle2" fontWeight={900} color={brand} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Grid container spacing={1.5}>
      {children}
    </Grid>
  </Box>
);

const DetailGridItem = ({ children }) => (
  <Grid item xs={12} sm={6}>
    {children}
  </Grid>
);

export default function ReconciliationPage() {
  const { user, groupId, loading: authLoading } = useAuth();
  const role = String(user?.role || "").trim().toLowerCase();
  const isAuthorized = ["admin", "mwenyekiti", "chairperson"].includes(role);

  const [reconcileDate, setReconcileDate] = useState(() => {
    const today = new Date();
    return [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, "0"),
      String(today.getDate()).padStart(2, "0"),
    ].join("-");
  });
  const [mode, setMode] = useState(() =>
    (process.env.REACT_APP_RECONCILIATION_MODE || "LIVE").toUpperCase(),
  );
  const [transactions, setTransactions] = useState([]);
  const [localOnlyTransactions, setLocalOnlyTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState(null);
  const [manualResult, setManualResult] = useState(null);

  const loadReconciliation = useCallback(async () => {
    if (!isAuthorized) return;
    setLoading(true);
    setFetchError(null);
    try {
      const payload = await fetchBankReconciliation({
        reconcileDate: toDdMmYyyy(reconcileDate),
        mode: mode === "SANDBOX" ? "sandbox" : undefined,
        groupId,
      });
      setTransactions(
        (Array.isArray(payload?.transactions) ? payload.transactions : []).map(
          normalizeReconciliationTransaction,
        ),
      );
      setLocalOnlyTransactions(
        (
          Array.isArray(payload?.local_only_transactions)
            ? payload.local_only_transactions
            : []
        ).map(normalizeReconciliationTransaction),
      );
      setPage(0);
    } catch (error) {
      setFetchError(error?.message || "Failed to fetch NMB reconciliation data.");
      setTransactions([]);
      setLocalOnlyTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [groupId, isAuthorized, mode, reconcileDate]);

  useEffect(() => {
    if (!authLoading && isAuthorized) loadReconciliation();
  }, [authLoading, isAuthorized, loadReconciliation]);

  const filteredTransactions = useMemo(
    () =>
      filterReconciliationTransactions(
        transactions,
        searchQuery,
        statusFilter,
      ),
    [searchQuery, statusFilter, transactions],
  );
  const paginatedTransactions = useMemo(
    () =>
      filteredTransactions.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredTransactions, page, rowsPerPage],
  );
  const totals = useMemo(
    () => summarizeReconciliation(transactions),
    [transactions],
  );

  const openDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setManualError(null);
    setManualResult(null);
  };

  const handleManualReconcile = async () => {
    if (!canManualReconcile(selectedTransaction)) return;
    setManualLoading(true);
    setManualError(null);
    setManualResult(null);
    try {
      const result = await manualReconcilePayment({
        reference: selectedTransaction.reference,
        receipt: selectedTransaction.receipt,
        amount: selectedTransaction.amount,
        timestamp: selectedTransaction.timestamp,
        channel: selectedTransaction.channel,
        groupId,
      });
      setManualResult(result);
      await loadReconciliation();
    } catch (error) {
      setManualError(error?.message || "Manual reconciliation failed.");
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={900} color={brand}>
          NMB Reconciliation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Complete bank activity for the selected date, enriched with Savvy processing evidence.
        </Typography>
      </Box>

      {!authLoading && !isAuthorized && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Admin or Chairperson access is required.
        </Alert>
      )}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {fetchError}
        </Alert>
      )}
      {mode === "SANDBOX" && isAuthorized && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Sandbox mode shows simulated bank evidence. Manual reconciliation still invokes the real payment engine.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Bank Transactions"
            value={totals.total}
            icon={<AccountBalanceIcon />}
            color={brand}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Bank Amount"
            value={formatMoney(totals.totalAmount)}
            icon={<MonetizationOnIcon />}
            color="#1565c0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Processed / Matched"
            value={totals.processed}
            icon={<CheckCircleOutlineIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Failed"
            value={totals.failed}
            icon={<ErrorOutlineIcon />}
            color="#d32f2f"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Unmatched"
            value={totals.unmatched}
            icon={<HelpOutlineIcon />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Suspense / Review"
            value={totals.needsReview}
            icon={<SyncAltIcon />}
            color="#7b1fa2"
          />
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Select
                fullWidth
                size="small"
                value={mode}
                onChange={(event) => setMode(event.target.value)}
                aria-label="Reconciliation mode"
              >
                <MenuItem value="LIVE">Live NMB API</MenuItem>
                <MenuItem value="SANDBOX">Sandbox</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Reconciliation date"
                value={reconcileDate}
                onChange={(event) => setReconcileDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search receipt, reference, or customer"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
                }
                onClick={loadReconciliation}
                disabled={!isAuthorized || loading}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
              >
                Fetch
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Tabs
          value={statusFilter}
          onChange={(_, value) => {
            setStatusFilter(value);
            setPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Reconciliation status filters"
          sx={{ px: 1 }}
        >
          {RECONCILIATION_FILTERS.map((filter) => (
            <Tab
              key={filter}
              value={filter}
              label={filter.replaceAll("_", " ")}
            />
          ))}
        </Tabs>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={850}>
            NMB-confirmed transactions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredTransactions.length} of {transactions.length} bank rows. Summary cards use the complete bank dataset.
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1350 }}>
            <TableHead>
              <TableRow>
                <TableCell>Transaction Time</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Savvy Status</TableCell>
                <TableCell>Reconciliation</TableCell>
                <TableCell>Allocation</TableCell>
                <TableCell>Failure</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>{formatDateTime(transaction.timestamp)}</TableCell>
                  <TableCell>{transaction.receipt || "-"}</TableCell>
                  <TableCell>{transaction.reference || "-"}</TableCell>
                  <TableCell>{transaction.customerName}</TableCell>
                  <TableCell>{transaction.channel}</TableCell>
                  <TableCell align="right">
                    {formatMoney(transaction.amount, transaction.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={transaction.savvyStatus}
                      color={chipColor(transaction.savvyStatus)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={transaction.reconciliationStatus}
                      color={chipColor(transaction.reconciliationStatus)}
                    />
                  </TableCell>
                  <TableCell>{transaction.allocationStatus}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography variant="caption" color="error" noWrap>
                      {transaction.failureCategory || transaction.failureReason || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View bank and Savvy evidence">
                      <IconButton
                        aria-label={`View ${transaction.receipt}`}
                        onClick={() => openDetails(transaction)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {canManualReconcile(transaction) && (
                      <Tooltip title="Review / manual reconcile">
                        <IconButton
                          aria-label={`Reconcile ${transaction.receipt}`}
                          color="primary"
                          onClick={() => openDetails(transaction)}
                        >
                          <SyncAltIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!paginatedTransactions.length && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    {loading ? "Fetching NMB transactions…" : "No bank transactions match this view."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredTransactions.length}
          page={page}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 2, mt: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={850}>
                Local-only / callback-only evidence
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Savvy records for this date that were not present in the NMB response. These are never counted as bank-confirmed rows.
              </Typography>
            </Box>
            <Chip label={localOnlyTransactions.length} color="default" />
          </Stack>
          {localOnlyTransactions.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Receipt</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Local status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localOnlyTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{formatDateTime(transaction.timestamp)}</TableCell>
                      <TableCell>{transaction.receipt || "-"}</TableCell>
                      <TableCell>{transaction.reference || "-"}</TableCell>
                      <TableCell>{transaction.savvyStatus}</TableCell>
                      <TableCell align="right">
                        {formatMoney(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openDetails(transaction)}>
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selectedTransaction)}
        onClose={() => !manualLoading && setSelectedTransaction(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Transaction evidence</DialogTitle>
        <DialogContent dividers>
          {manualError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {manualError}
            </Alert>
          )}
          {manualResult && (
            <Alert
              severity={String(manualResult.status) === "1" ? "success" : "warning"}
              sx={{ mb: 2 }}
            >
              {manualResult.description || "Manual reconciliation completed."}
              {manualResult.idempotent ? " No duplicate allocation was created." : ""}
            </Alert>
          )}
          {selectedTransaction && (
            <Stack spacing={3}>
              <DetailSection title="BANK EVIDENCE">
                <DetailGridItem><DetailField label="Receipt" value={selectedTransaction.receipt} /></DetailGridItem>
                <DetailGridItem><DetailField label="Reference / control number" value={selectedTransaction.reference} /></DetailGridItem>
                <DetailGridItem><DetailField label="Amount" value={formatMoney(selectedTransaction.amount, selectedTransaction.currency)} /></DetailGridItem>
                <DetailGridItem><DetailField label="Timestamp" value={formatDateTime(selectedTransaction.timestamp)} /></DetailGridItem>
                <DetailGridItem><DetailField label="Customer" value={selectedTransaction.customerName} /></DetailGridItem>
                <DetailGridItem><DetailField label="Account number" value={selectedTransaction.accountNumber} /></DetailGridItem>
                <DetailGridItem><DetailField label="Channel" value={selectedTransaction.channel} /></DetailGridItem>
                <DetailGridItem><DetailField label="Evidence status" value={selectedTransaction.bankEvidenceStatus} /></DetailGridItem>
              </DetailSection>
              <Divider />
              <DetailSection title="SAVVY PROCESSING">
                <DetailGridItem><DetailField label="BankPayment ID" value={selectedTransaction.savvyPayment.id} /></DetailGridItem>
                <DetailGridItem><DetailField label="Processing status" value={selectedTransaction.savvyStatus} /></DetailGridItem>
                <DetailGridItem><DetailField label="Source" value={selectedTransaction.savvyPayment.source} /></DetailGridItem>
                <DetailGridItem><DetailField label="Processed at" value={formatDateTime(selectedTransaction.savvyPayment.processedAt)} /></DetailGridItem>
                <DetailGridItem><DetailField label="Failure category" value={selectedTransaction.failureCategory} /></DetailGridItem>
                <DetailGridItem><DetailField label="Failure reason" value={selectedTransaction.failureReason} /></DetailGridItem>
              </DetailSection>
              <Divider />
              <DetailSection title="PAYMENT CHARGES (SNAPSHOT)">
                <DetailGridItem><DetailField label="Gross received" value={formatMoney(selectedTransaction.charges.gross)} /></DetailGridItem>
                <DetailGridItem><DetailField label="SSC rate used" value={selectedTransaction.charges.sscRate !== null ? `${selectedTransaction.charges.sscRate}%` : "-"} /></DetailGridItem>
                <DetailGridItem><DetailField label="SSC amount" value={formatMoney(selectedTransaction.charges.sscAmount)} /></DetailGridItem>
                <DetailGridItem><DetailField label="Bank charge rate used" value={selectedTransaction.charges.bankChargeRate !== null ? `${selectedTransaction.charges.bankChargeRate}%` : "-"} /></DetailGridItem>
                <DetailGridItem><DetailField label="Bank charge amount" value={formatMoney(selectedTransaction.charges.bankChargeAmount)} /></DetailGridItem>
                <DetailGridItem><DetailField label="Net allocatable" value={formatMoney(selectedTransaction.charges.netAllocatable)} /></DetailGridItem>
              </DetailSection>
              <Divider />
              <DetailSection title="RECONCILIATION">
                <DetailGridItem><DetailField label="Classification" value={selectedTransaction.reconciliationStatus} /></DetailGridItem>
                <DetailGridItem><DetailField label="Allocation status" value={selectedTransaction.allocationStatus} /></DetailGridItem>
                <DetailGridItem><DetailField label="Reconciled by" value={selectedTransaction.reconciledBy} /></DetailGridItem>
              </DetailSection>
              {selectedTransaction.allocationBreakdown.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={900} color={brand} sx={{ mb: 1 }}>
                      ALLOCATION BREAKDOWN
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Target</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedTransaction.allocationBreakdown.map((item, index) => (
                            <TableRow key={`${item?.type || "allocation"}-${index}`}>
                              <TableCell>{item?.type || item?.obligation_type || "-"}</TableCell>
                              <TableCell>{item?.target || item?.id || "-"}</TableCell>
                              <TableCell align="right">{formatMoney(item?.amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTransaction(null)} disabled={manualLoading}>
            Close
          </Button>
          {canManualReconcile(selectedTransaction) && (
            <Button
              variant="contained"
              onClick={handleManualReconcile}
              disabled={manualLoading}
              startIcon={manualLoading ? <CircularProgress size={16} color="inherit" /> : <SyncAltIcon />}
              sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            >
              {manualLoading ? "Reconciling…" : "Manual Reconcile"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
